import { ChangeDetectorRef, Component, Injector } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { I18nService } from '@recruitee/i18n';
import { DialogComponentI, DialogRef, WindowRef } from '@recruitee/user-interface';

import { WysiwygLinkType } from '../wysiwyg-link.types';
import { LinkInsertDialogData } from './types';

@Component({
  selector: 'rt-redactor-inserts-dialog',
  templateUrl: './redactor-inserts-dialog.component.html',
})
export class RedactorInsertsDialogComponent
  implements DialogComponentI<LinkInsertDialogData, null>
{
  public loading: boolean = false;
  public form: UntypedFormGroup;
  public containerDataSnapshot: LinkInsertDialogData;
  public gdprConsentLinkType = WysiwygLinkType.GdprConsentRequest;
  public isValidLink = false;
  public placeholder: string;
  public isDelayedEdit = false;
  public heading: string;

  public readonly WYSIWYG_LINK_PLACEHOLDER_MAP: Partial<Record<WysiwygLinkType, string>> = {
    [WysiwygLinkType.Questionnaires]: this.i18n.translate(
      'app.redactor_inserts.questionnaires.dialog.placeholder',
    ),
    [WysiwygLinkType.Scheduler]: this.i18n.translate(
      'app.redactor_inserts.scheduler.dialog.placeholder',
    ),
  };

  private ngOnDestroy$ = new Subject<void>();

  constructor(
    public dialog: DialogRef,
    private i18n: I18nService,
    private fb: UntypedFormBuilder,
    protected injector: Injector,
    protected cdRef: ChangeDetectorRef,
    protected winRef: WindowRef,
  ) {
    this.form = this.fb.group({
      link: [this.containerDataSnapshot?.link, Validators.required],
      label: [
        this.containerDataSnapshot?.label || this.setDefaultLabel(this.containerDataSnapshot?.type),
      ],
      period: [+this.containerDataSnapshot?.expirationDays!, [Validators.required]],
    });

    this.form.valueChanges.pipe(takeUntil(this.ngOnDestroy$.asObservable())).subscribe(value => {
      this.cdRef.detectChanges();
    });
    this.form
      .get('period')!
      .valueChanges.pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(value => {
        if (typeof +value !== 'number') {
          this.form.get('period')!.patchValue(0);
        } else {
          if (!this.isDelayedEdit) {
            this.updatePeriodInLink(this.containerDataSnapshot?.type, value.toString());
          }
        }
      });
  }

  public isPlaceholderLink(link: string) {
    return /.*\/v\/gdpr\/consent\/.*\/no-token/.test(link);
  }

  public setDialogData(containerData: LinkInsertDialogData, _triggerUpdate: boolean): void {
    this.containerDataSnapshot = containerData;
    this.setHeading(containerData);
    this.placeholder = this.setPlaceholder(containerData.type, containerData.link);
    this.isValidLink = !this.isPlaceholder(containerData.link);
    this.isDelayedEdit = !this.isPlaceholderLink(containerData.link);
    this.form?.patchValue({
      link: this.containerDataSnapshot.link,
      label:
        this.containerDataSnapshot?.label || this.setDefaultLabel(this.containerDataSnapshot?.type),
      period: this.containerDataSnapshot.expirationDays
        ? +this.containerDataSnapshot?.expirationDays!
        : 0,
    });
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public submit(): void {
    if (this.form.valid) {
      this.dialog.close({
        ...this.form.value,
        openInNewTab: true,
        type: this.containerDataSnapshot.type,
        label: this.form.value.label || this.setDefaultLabel(this.containerDataSnapshot.type),
      });
    }
  }

  public removeLink(): void {
    this.dialog.close({
      delete: true,
    });
  }

  public linkOut() {
    this.winRef.open(this.form.value.link);
  }

  public isPlaceholder(link: string): boolean {
    return /^\[.*\]/.test(link);
  }

  private isLinkInPlaceholder(type: WysiwygLinkType): boolean {
    switch (type) {
      case WysiwygLinkType.Questionnaires:
        return true;
      case WysiwygLinkType.Scheduler:
        return true;
      default:
        return false;
    }
  }

  private setPlaceholder(type: WysiwygLinkType, link: string) {
    return (
      this.WYSIWYG_LINK_PLACEHOLDER_MAP[type] ||
      (this.isLinkInPlaceholder(type) ? link : this.setDefaultLabel(type))
    );
  }

  private updatePeriodInLink(type: WysiwygLinkType, value: string): void {
    if (type === WysiwygLinkType.GdprConsentRequest && this.form.value.link) {
      const link: string = this.form.value.link;

      this.form.patchValue({ link: link.replace(/\d+/g, value || '0') });
    }
  }

  private setDefaultLabel(type: WysiwygLinkType) {
    switch (type) {
      case WysiwygLinkType.GdprConsentRequest:
        return this.i18n.translate(`app.redactor_inserts.gdpr.dialog.consent_request_default`);
      case WysiwygLinkType.GdprRemove:
        return this.i18n.translate(`app.redactor_inserts.gdpr.dialog.removal_default`);
      case WysiwygLinkType.GdprChange:
        return this.i18n.translate(`app.redactor_inserts.gdpr.dialog.change_default`);
      case WysiwygLinkType.IntegrationsKununuApplicantFeedback:
        return this.i18n.translate(
          `app.redactor_inserts.integrations_kununu.dropdown.applicant_feedback`,
        );
      case WysiwygLinkType.IntegrationsKununuEmployeeFeedback:
        return this.i18n.translate(
          `app.redactor_inserts.integrations_kununu.dropdown.employee_feedback`,
        );
      default:
        return '';
    }
  }

  private setHeading(containerData: LinkInsertDialogData) {
    if (containerData.heading) {
      this.heading = containerData.heading;
      return;
    }
    this.heading = containerData.isEdit
      ? this.i18n.translate('app.wysiwyg.dialog.heading_edit')
      : this.i18n.translate('app.wysiwyg.dialog.heading');
  }
}
